import React, { useState, useEffect } from 'react'
import { registerWithEmail, getInviteDetails } from '../../api/api'
import { RegistrationResponse } from '../../api/models'
import styles from './Auth.module.css'
import Button from '../ui/Button'

export const getPasswordValidation = (password: string) => {
    return {
        minLength: password.length >= 8,
        hasUpperCase: /[A-Z]/.test(password),
        hasLowerCase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    }
}

const isPasswordValid = (password: string) => {
    const validation = getPasswordValidation(password)
    return Object.values(validation).every((v) => v)
}

interface RegistrationFormProps {
    onSuccess: (data: RegistrationResponse) => void
    onSwitchToLogin: () => void
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
    onSuccess,
    onSwitchToLogin,
}) => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [acceptedTerms, setAcceptedTerms] = useState(false)
    const [canContact, setCanContact] = useState(false)
    const [inviteCode, setInviteCode] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [isEmailLocked, setIsEmailLocked] = useState(false)
    const [isInviteCodeLocked, setIsInviteCodeLocked] = useState(false)
    const [loadingInvite, setLoadingInvite] = useState(false)
    const [inviteMessage, setInviteMessage] = useState('')
    const [showPasswordRequirements, setShowPasswordRequirements] =
        useState(false)
    const termsandconditionslink = 'https://www.lorefully.com/privacy-policy'

    // Check for invite code and email in URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const inviteParam = urlParams.get('invite')
        const emailParam = urlParams.get('email')

        // Pre-fill email from URL if provided
        if (emailParam) {
            setEmail(emailParam)
            setIsEmailLocked(true)
        }

        if (inviteParam) {
            setLoadingInvite(true)
            setInviteCode(inviteParam)
            setIsInviteCodeLocked(true)

            // Fetch invite details
            getInviteDetails(inviteParam)
                .then((details) => {
                    if (details.valid) {
                        // If email-specific invite, pre-fill and lock email field
                        if (details.email && !emailParam) {
                            setEmail(details.email)
                            setIsEmailLocked(true)
                            setInviteMessage(
                                'Welcome! Your email has been pre-filled from your invite. Just create a password to complete registration.'
                            )
                        } else {
                            setInviteMessage(
                                emailParam
                                    ? 'Welcome! Your email has been pre-filled from your invite. Just create a password to complete registration.'
                                    : 'Invite code applied! Enter your email and password to complete registration.'
                            )
                        }
                    } else {
                        setError(
                            details.error ||
                                'Invalid or expired invite code. You can still register if open registration is enabled.'
                        )
                        setIsInviteCodeLocked(false)
                    }
                })
                .catch((err) => {
                    setError(
                        'Failed to validate invite code. You can still try to register.'
                    )
                    setIsInviteCodeLocked(false)
                })
                .finally(() => {
                    setLoadingInvite(false)
                })
        }
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        // Basic validation
        if (!email || !password || !firstName || !lastName) {
            setError('Please fill in all required fields')
            return
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match')
            return
        }

        if (!isPasswordValid(password)) {
            setError("Password doesn't meet all requirements")
            return
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters')
            return
        }

        if (!acceptedTerms) {
            setError('You must accept the terms and conditions')
            return
        }

        setLoading(true)

        try {
            const response = await registerWithEmail(
                email,
                password,
                firstName,
                lastName,
                acceptedTerms,
                canContact,
                undefined, // anonymousId will be read from HTTP-only cookie by backend
                inviteCode || undefined
            )
            onSuccess(response)
        } catch (err: any) {
            // Handle 409 conflict (email already registered) - suggest login instead
            if (err.message && err.message.includes('already registered')) {
                setError(
                    'This email is already registered. Please sign in instead.'
                )
            } else if (
                err.message &&
                err.message.includes('requires pre-approval')
            ) {
                setError(
                    'Registration requires admin approval. Please use an invite code or contact an administrator.'
                )
            } else {
                setError(
                    err.message || 'Registration failed. Please try again.'
                )
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>Create Your Account</h2>

            {loadingInvite && (
                <div className={styles.notice}>Loading invite details...</div>
            )}

            {inviteMessage && !loadingInvite && (
                <div
                    className={styles.notice}
                    style={{
                        background: 'var(--color-info-bg)',
                        color: 'var(--color-info)',
                    }}
                >
                    {inviteMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.field}>
                    <label htmlFor="firstName">First Name*</label>
                    <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Your first name"
                        disabled={loading}
                        required
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="lastName">Last Name*</label>
                    <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Your last name"
                        disabled={loading}
                        required
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="email">
                        Email*
                        {isEmailLocked && (
                            <span
                                style={{
                                    fontWeight: 'normal',
                                    fontSize: '0.9em',
                                    marginLeft: '8px',
                                }}
                            >
                                (from invite)
                            </span>
                        )}
                    </label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        disabled={loading || loadingInvite}
                        readOnly={isEmailLocked}
                        required
                        style={
                            isEmailLocked
                                ? {
                                      background: '#F9FAFB',
                                      cursor: 'not-allowed',
                                  }
                                : {}
                        }
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="password">Password*</label>
                    <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setShowPasswordRequirements(true)}
                        placeholder="At least 8 characters"
                        disabled={loading}
                        required
                    />
                    {showPasswordRequirements && password && (
                        <PasswordRequirements password={password} />
                    )}
                </div>

                <div className={styles.field}>
                    <label htmlFor="confirmPassword">Confirm Password*</label>
                    <input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        disabled={loading}
                        required
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="inviteCode">
                        Invite Code{' '}
                        {!isInviteCodeLocked && (
                            <span
                                style={{
                                    fontWeight: 'normal',
                                    fontSize: '0.9em',
                                }}
                            >
                                (Optional)
                            </span>
                        )}
                        {isInviteCodeLocked && (
                            <span
                                style={{
                                    fontWeight: 'normal',
                                    fontSize: '0.9em',
                                    marginLeft: '8px',
                                }}
                            >
                                (from link)
                            </span>
                        )}
                    </label>
                    <input
                        id="inviteCode"
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        placeholder="Enter invite code if you have one"
                        disabled={loading || loadingInvite}
                        readOnly={isInviteCodeLocked}
                        style={
                            isEmailLocked
                                ? {
                                      background: 'var(--color-bg-secondary)',
                                      cursor: 'not-allowed',
                                  }
                                : {}
                        }
                    />
                </div>

                <div className={styles.field}>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            id="acceptedTerms"
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            disabled={loading}
                            required
                        />
                        <span>
                            I accept the{' '}
                            <a
                                href={termsandconditionslink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                            >
                                terms and conditions
                            </a>
                            *
                        </span>
                    </label>
                </div>

                <div className={styles.field}>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            id="canContact"
                            type="checkbox"
                            checked={canContact}
                            onChange={(e) => setCanContact(e.target.checked)}
                            disabled={loading}
                        />
                        <span>
                            I agree to be contacted about updates and news
                        </span>
                    </label>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                <Button type="submit" variant="primary" fullWidth disabled={loading}>
                    {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
            </form>

            <div className={styles.footer}>
                Already have an account?{' '}
                <button onClick={onSwitchToLogin} className={styles.linkButton}>
                    Sign in
                </button>
            </div>
        </div>
    )
}

export const PasswordRequirements: React.FC<{ password: string }> = ({ password }) => {
    const validation = getPasswordValidation(password)

    return (
        <div
            style={{
                fontSize: '0.85em',
                marginTop: '8px',
                padding: '8px',

                borderRadius: '4px',
            }}
        >
            <div style={{ marginBottom: '4px', fontWeight: 500 }}>
                Password must contain:
            </div>
            {[
                { key: 'minLength', label: 'At least 8 characters' },
                { key: 'hasUpperCase', label: 'One uppercase letter' },
                { key: 'hasLowerCase', label: 'One lowercase letter' },
                { key: 'hasNumber', label: 'One number' },
                { key: 'hasSpecial', label: 'One special character' },
            ].map(({ key, label }) => (
                <div
                    key={key}
                    style={{
                        color: validation[key as keyof typeof validation]
                            ? 'var(--color-text-brand)'
                            : 'var(--color-text-secondary)',
                        marginLeft: '4px',
                    }}
                >
                    {validation[key as keyof typeof validation] ? '✓' : '○'}{' '}
                    {label}
                </div>
            ))}
        </div>
    )
}
